import { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { usePhotos } from '../../hooks/usePhotos';
import { compressImageWithCrop } from '../../lib/imageUtils';
import { fixedDomain, formatLabel, isValidItemKey, matchExistingLabel } from '../../lib/huntDomain';
import styles from './AddPhotoModal.module.css';

// ── Crop Editor ───────────────────────────────────────────────────────────────

function CropEditor({ file, onConfirm, onCancel }) {
  const containerRef = useRef();
  const imgRef       = useRef();
  const dragRef      = useRef(null);

  const [containerSize, setContainerSize] = useState(0); // set after layout
  const [zoom,          setZoom]          = useState(1);
  const [offset,        setOffset]        = useState({ x: 0, y: 0 });

  const [imgSrc, setImgSrc] = useState('');
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => setImgSrc(e.target.result);
    reader.readAsDataURL(file);
  }, [file]);

  // Measure container size after mount (avoids reading offsetWidth=0 on first paint)
  useEffect(() => {
    if (!containerRef.current) return;
    const size = containerRef.current.offsetWidth;
    if (size > 0) setContainerSize(size);
  }, []);

  function getBaseScale(img, size) {
    return Math.max(size / img.naturalWidth, size / img.naturalHeight);
  }

  function clamp(ox, oy, img, size, z) {
    const base = getBaseScale(img, size);
    const dw = img.naturalWidth  * base * z;
    const dh = img.naturalHeight * base * z;
    return {
      x: Math.min(0, Math.max(size - dw, ox)),
      y: Math.min(0, Math.max(size - dh, oy)),
    };
  }

  function handlePointerDown(e) {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  }

  function handlePointerMove(e) {
    if (!dragRef.current || !imgRef.current || containerSize === 0) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clamp(dragRef.current.ox + dx, dragRef.current.oy + dy, imgRef.current, containerSize, zoom));
  }

  function handlePointerUp() {
    dragRef.current = null;
  }

  function handleZoom(e) {
    const z = parseFloat(e.target.value);
    if (!imgRef.current || containerSize === 0) { setZoom(z); return; }
    setOffset(prev => clamp(prev.x, prev.y, imgRef.current, containerSize, z));
    setZoom(z);
  }

  function handleImgLoad() {
    // Re-read container size here in case it was 0 during the useEffect
    const size = containerRef.current?.offsetWidth ?? 0;
    const measured = size > 0 ? size : containerSize;
    if (measured === 0) return;
    setContainerSize(measured);

    const img  = imgRef.current;
    const base = getBaseScale(img, measured);
    const dw   = img.naturalWidth  * base;
    const dh   = img.naturalHeight * base;
    setOffset({ x: (measured - dw) / 2, y: (measured - dh) / 2 });
  }

  async function handleConfirm() {
    const img  = imgRef.current;
    const size = containerSize || containerRef.current?.offsetWidth;
    const base = getBaseScale(img, size);
    const sw   = size / (base * zoom);
    const sh   = size / (base * zoom);
    const sx   = (-offset.x) / (base * zoom);
    const sy   = (-offset.y) / (base * zoom);
    const result = await compressImageWithCrop(img, { sx, sy, sw, sh });
    onConfirm(result);
  }

  // Image display style — only computable once containerSize and image are known
  const imgStyle = containerSize > 0 && imgRef.current?.naturalWidth > 0 ? {
    position:      'absolute',
    width:         imgRef.current.naturalWidth  * getBaseScale(imgRef.current, containerSize) * zoom,
    height:        imgRef.current.naturalHeight * getBaseScale(imgRef.current, containerSize) * zoom,
    maxWidth:      'none',
    maxHeight:     'none',
    left:          offset.x,
    top:           offset.y,
    userSelect:    'none',
    pointerEvents: 'none',
    touchAction:   'none',
  } : { display: 'none' };

  return (
    <div className={styles.cropWrap}>
      <p className={styles.cropHint}>PINCH TO CROP · SQUARE</p>

      <div
        ref={containerRef}
        className={styles.cropCanvas}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        <img
          ref={imgRef}
          src={imgSrc}
          alt="Crop"
          style={imgStyle}
          onLoad={handleImgLoad}
          draggable={false}
        />
        {/* Corner guides */}
        <div className={styles.cropGuide} />
      </div>

      <input
        type="range"
        min="1"
        max="4"
        step="0.02"
        value={zoom}
        onChange={handleZoom}
        className={styles.zoomSlider}
        aria-label="Zoom"
      />
      <div className={styles.cropActions}>
        <button className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
        <button className={styles.saveBtn} onClick={handleConfirm}>Use this crop</button>
      </div>
    </div>
  );
}

// ── Add Photo Modal ───────────────────────────────────────────────────────────

export default function AddPhotoModal() {
  const { state, dispatch, derived, activeHunt } = useAppContext();
  const { addPhoto, setPrimary } = usePhotos();
  const { locationsList, photosByItem } = derived;

  const huntType = activeHunt?.type;
  const initialItemKey = state.modalData?.itemKey ?? (huntType === 'numbers' ? '1' : huntType === 'alphabet' ? 'A' : '');
  const [itemKey,  setItemKey]  = useState(String(initialItemKey));
  const [location, setLocation] = useState('');
  const [filePath, setFilePath] = useState('');
  const [file,     setFile]     = useState(null);
  const [cropping, setCropping] = useState(false);
  const [precompressed, setPrecompressed] = useState(null); // { blob, dataUrl }
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [makePrimary, setMakePrimary] = useState(true);
  const [matchedLabel, setMatchedLabel] = useState(null);
  const fileRef = useRef();
  const locationRef = useRef();

  if (!activeHunt) return null;

  const existingItemLabels = [...photosByItem.keys()].filter(k => (photosByItem.get(k) ?? []).length > 0);
  const isFirstForItem = (photosByItem.get(itemKey) ?? []).length === 0;

  const recentPlaces = [...state.photos]
    .filter(p => p.huntId === activeHunt.id)
    .sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
    .reduce((acc, p) => {
      if (!acc.includes(p.location)) acc.push(p.location);
      return acc;
    }, [])
    .slice(0, 6);

  function close() {
    dispatch({ type: 'CLOSE_MODAL' });
  }

  function handleFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setFilePath(f.name);
    setPrecompressed(null);
    setCropping(true);
  }

  function handleCropConfirm(result) {
    setPrecompressed(result);
    setCropping(false);
  }

  function handleCropCancel() {
    setFile(null);
    setPrecompressed(null);
    setCropping(false);
    // Reset the file input so the same file can be re-selected
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleItemKeyChange(raw) {
    setItemKey(raw);
    if (huntType !== 'custom') { setMatchedLabel(null); return; }
    setMatchedLabel(matchExistingLabel(raw, existingItemLabels));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    let key = itemKey;
    if (huntType === 'alphabet') key = key.trim().toUpperCase();
    if (huntType === 'custom') {
      key = key.trim();
      const existing = matchExistingLabel(key, existingItemLabels);
      if (existing) key = existing; // snap to existing canonical casing
    }

    if (!isValidItemKey(huntType, key)) {
      setError(huntType === 'numbers' ? 'Number must be 1–100' : huntType === 'alphabet' ? 'Must be a letter A–Z' : 'Item name is required');
      return;
    }
    if (!location.trim()) { setError('Location is required'); return; }
    setError('');
    setSaving(true);
    try {
      const photo = await addPhoto({ itemKey: key, location, file: null, filePath, precompressed });
      if (makePrimary && !isFirstForItem) {
        await setPrimary(key, photo.id);
      }
      close();
    } catch (err) {
      setError('Something went wrong. Try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  if (cropping && file) {
    return (
      <div className={styles.backdrop}>
        <div className={styles.panel}>
          <div className={styles.header}>
            <h2 className={styles.title}>Adjust Crop</h2>
          </div>
          <CropEditor
            file={file}
            onConfirm={handleCropConfirm}
            onCancel={handleCropCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && close()}>
      <div className={styles.panel}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {state.modalData?.itemKey ? `Add Photo — ${formatLabel(huntType, state.modalData.itemKey)}` : 'Add Photo'}
          </h2>
          <button className={styles.closeBtn} onClick={close} aria-label="Close">✕</button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Item */}
          {huntType === 'numbers' && (
            <label className={styles.label}>
              Number (1–100)
              <input
                className={styles.input}
                type="number"
                min="1"
                max="100"
                value={itemKey}
                onChange={e => handleItemKeyChange(e.target.value)}
                required
              />
            </label>
          )}

          {huntType === 'alphabet' && (
            <label className={styles.label}>
              Letter
              <select
                className={styles.input}
                value={itemKey}
                onChange={e => handleItemKeyChange(e.target.value)}
                required
              >
                {fixedDomain('alphabet').map(letter => (
                  <option key={letter} value={letter}>{letter}</option>
                ))}
              </select>
            </label>
          )}

          {huntType === 'custom' && (
            <label className={styles.label}>
              Item name
              <input
                className={styles.input}
                type="text"
                list="item-options"
                value={itemKey}
                onChange={e => handleItemKeyChange(e.target.value)}
                placeholder="e.g. Manhole Cover"
                required
              />
              <datalist id="item-options">
                {existingItemLabels.map(k => <option key={k} value={k} />)}
              </datalist>
              {matchedLabel && (
                <span className={styles.matchHint}>Matched existing item: <strong>{matchedLabel}</strong></span>
              )}
            </label>
          )}

          {/* Photo upload */}
          <div className={styles.label}>
            <span>Photo</span>
            <div
              className={styles.dropZone}
              onClick={() => { if (!precompressed) fileRef.current?.click(); }}
            >
              {precompressed
                ? <img src={precompressed.dataUrl} alt="Preview" className={styles.previewImg} />
                : <span className={styles.dropHint}>Tap to choose a photo</span>
              }
            </div>
            {precompressed && (
              <button
                type="button"
                className={styles.recropBtn}
                onClick={() => file && setCropping(true)}
              >
                Adjust crop
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className={styles.hiddenInput}
          />

          {/* Location */}
          <label className={styles.label}>
            Location <span className={styles.required}>*</span>
            <input
              ref={locationRef}
              className={`${styles.input} ${styles.placeInput}`}
              type="text"
              list="location-options"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g. Brooklyn Bridge"
              required
            />
            <datalist id="location-options">
              {locationsList.map(loc => (
                <option key={loc} value={loc} />
              ))}
            </datalist>
            {recentPlaces.length > 0 && (
              <div className={styles.placeChips}>
                {recentPlaces.map(loc => (
                  <button
                    key={loc}
                    type="button"
                    className={styles.placeChip}
                    onClick={() => setLocation(loc)}
                  >
                    {loc}
                  </button>
                ))}
                <button
                  type="button"
                  className={`${styles.placeChip} ${styles.placeChipNew}`}
                  onClick={() => { setLocation(''); locationRef.current?.focus(); }}
                >
                  + NEW
                </button>
              </div>
            )}
          </label>

          {/* Make primary toggle */}
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={isFirstForItem || makePrimary}
              disabled={isFirstForItem}
              onChange={e => setMakePrimary(e.target.checked)}
            />
            <span>Make this the primary shot</span>
          </label>

          {/* File path */}
          <label className={styles.label}>
            <span className={styles.optionalLabel}>
              Original file path <span className={styles.optional}>(optional)</span>
            </span>
            <input
              className={`${styles.input} ${styles.mono}`}
              type="text"
              value={filePath}
              onChange={e => setFilePath(e.target.value)}
              placeholder="/photos/NYC/IMG_4892.jpg"
            />
          </label>

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={close}>
              Cancel
            </button>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? 'Saving…' : 'Save Photo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
